import type { CurationStatus } from '@packages/enums';
import type { DocumentStatus, FileProgress, FileStatus, StageDetails, TimingInfo } from './data-curation-result';
import type { Ontology, OntologyFields } from './ontology';

export type OntologyConfiguration = Ontology;

export interface DocRankerConfig {
    cancer_type?: string;
    infamily_penalty?: number;
    resolved_true?: number;
    metadata_multiplier?: number;
}

export interface DataCurationJob {
    id: string;
    name: string;
    description: string | null;
    owner_id: string;
    dataset_id: string;
    dataset?: {
        id: string;
        name: string;
        description?: string;
    };
    created_at: string;
    updated_at: string;
    status: CurationStatus;
    error_message?: string | null;
    patientCount?: number;
    recordCount?: number;
    progress?: number;
    completedResultsCount?: number;
    failedResultsCount?: number;
    // Document-level tracking fields (from document statuses)
    total_documents?: number;
    completed_documents?: number;
    failed_documents?: number;
    documents?: Record<string, DocumentStatus>;
    // Stage and progress tracking (from Python service)
    message?: string;
    stage?: string;
    stage_details?: StageDetails;
    file_status?: FileStatus[];
    file_progress?: FileProgress;
    timing?: TimingInfo;
    // Ontology fields
    ontology_id?: string | null;
    ontology: Ontology; // Display name from backend
    extraction_method: string;
    document_ranking?: boolean;
}

export interface TestExtractionRequest {
    ontology_name: string;
    is_preset?: boolean;
    output_schema?: string | null;
    fields: TestExtractionField[];
    patient_id: number;
    document_ids: number[];
}

export interface TestExtractionField {
    name: string;
    description?: string | null;
    data_type?: string | null;
    category?: string | null;
    extraction_instruction?: string | null;
}

export interface TestExtractionResponse {
    session_id: string;
}

export interface TestExtractionStatusResponse {
    status: string;
    progress?: number;
    stage?: string;
    error?: string;
    file_status?: Array<{
        file_path: string;
        stage: string;
        status: string;
        file_completed: boolean;
    }>;
}

export interface CreateDataCurationRequest {
    name: string;
    description?: string;
    // Ontology fields
    ontology_id?: string;
    ontology_config?: OntologyConfiguration;
    extraction_method?: string;
    // Dataset integration
    dataset_id?: string;
    // Document ranking (DocRanker)
    document_ranking?: boolean;
    doc_ranker_config?: DocRankerConfig;
}

export interface UpdateDataCurationRequest {
    name?: string;
    description?: string;

    // Ontology fields
    ontology_id?: string;
    ontology_config?: OntologyConfiguration;
    extraction_method?: string;
}

export interface DataCurationJobListResponse {
    jobs: DataCurationJob[];
    total: number;
}

export interface NewCurationFormValues {
    name: string;
    description?: string;
    cohorts: string[];
    filterParams?: {
        search?: string;
        minAge?: number;
        maxAge?: number;
        gender?: string;
        recordTypes?: string[];
        startDate?: string;
        endDate?: string;
    };
    records: string[];
    selectedOntology: string;
    customOntology?: {
        name: string;
        description: string;
        configuration: OntologyFields;
    };
    extractionMethod: string;
    // Document ranking (DocRanker)
    document_ranking?: boolean;
    doc_ranker_config?: DocRankerConfig;
}
